import { appBuildSchema } from '@speaking-coach/shared-types/src/app-build.js';
/** Public artifact metadata only. No API version, semantic ordering or auth data. */
export function buildIdentity(value) {
    const parsed = appBuildSchema.safeParse(value);
    if (!parsed.success)
        return null;
    const { release, gitSha, builtAt } = parsed.data;
    return JSON.stringify([release, gitSha, builtAt]);
}
/** Separate from API transports: public, same-origin static build metadata. */
export async function readAppVersion(signal) {
    const response = await fetch('/version.json', { signal, cache: 'no-store', credentials: 'same-origin', redirect: 'error' });
    if (!response.ok || !response.headers.get('content-type')?.includes('application/json'))
        throw new Error('Build metadata unavailable');
    return response.json();
}
export const UPDATE_CHECK_INTERVAL_MS = 60_000;
export const UPDATE_CHECK_THROTTLE_MS = 60_000;
export const UPDATE_SNOOZE_MS = 30 * 60_000;
export function createUpdateMonitor({ running, load, ignored = [], snoozed = {}, persist = () => { }, persistSnoozed = () => { } }) {
    const identity = buildIdentity(running);
    const attempted = new Set(ignored);
    const deadlines = new Map(Object.entries(snoozed));
    let wakeTimer;
    const listeners = new Set();
    let candidate = null;
    let snapshot = null;
    let lastCheck = -Infinity;
    let flight = null;
    let controller = null;
    let disposed = false;
    const emit = () => { listeners.forEach(listener => listener()); };
    const getSnapshot = () => snapshot;
    const publish = () => {
        clearTimeout(wakeTimer);
        if (disposed)
            return;
        const remaining = candidate && !attempted.has(candidate) ? (deadlines.get(candidate) ?? 0) - Date.now() : 0;
        if (remaining > 0)
            wakeTimer = setTimeout(publish, remaining);
        const next = candidate && !attempted.has(candidate) && remaining <= 0 ? candidate : null;
        if (next !== snapshot) {
            snapshot = next;
            emit();
        }
    };
    const snooze = () => {
        if (disposed || !getSnapshot() || !candidate)
            return;
        for (const [key, until] of deadlines)
            if (until <= Date.now())
                deadlines.delete(key);
        deadlines.set(candidate, Date.now() + UPDATE_SNOOZE_MS);
        persistSnoozed(Object.fromEntries(deadlines));
        publish();
    };
    return {
        getSnapshot,
        subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
        check(force = false) {
            if (disposed || !identity)
                return Promise.resolve();
            // Resume can cross a deadline even when the browser suspended timers.
            publish();
            if (flight)
                return flight;
            if (!force && Date.now() - lastCheck < UPDATE_CHECK_THROTTLE_MS)
                return Promise.resolve();
            lastCheck = Date.now();
            const request = new AbortController();
            controller = request;
            flight = (async () => {
                let timer;
                try {
                    const timeout = new Promise((_, reject) => {
                        timer = setTimeout(() => { request.abort(); reject(new Error('Version check timed out')); }, 5000);
                    });
                    const latest = buildIdentity(await Promise.race([load(request.signal), timeout]));
                    if (!disposed && !request.signal.aborted && latest) {
                        candidate = latest === identity ? null : latest;
                        publish();
                    }
                }
                catch { /* Offline, rollout gaps and bad responses never obstruct use. */ }
                finally {
                    clearTimeout(timer);
                }
            })();
            // Clear after assignment even if a custom loader throws synchronously.
            void flight.then(() => { flight = null; controller = null; });
            return flight;
        },
        snooze,
        refresh(canRefresh, reload) {
            if (!getSnapshot() || disposed || !canRefresh())
                return;
            // Persist before navigating. If stale HTML loads again, do not prompt in a loop.
            attempted.add(candidate);
            persist([...attempted]);
            publish();
            reload();
        },
        dispose() { disposed = true; clearTimeout(wakeTimer); controller?.abort(); candidate = null; snapshot = null; listeners.clear(); },
    };
}
/** Each app owns its instance. A lease begins synchronously, before a mutation. */
export function createRefreshSafety() {
    const owners = new Set();
    const listeners = new Set();
    const emit = () => { listeners.forEach(listener => listener()); };
    const hold = () => {
        const owner = Symbol();
        owners.add(owner);
        emit();
        return () => { if (owners.delete(owner))
            emit(); };
    };
    const during = async (operation) => {
        const release = hold();
        try {
            return await operation();
        }
        finally {
            release();
        }
    };
    return {
        hold,
        isSafe: () => owners.size === 0,
        subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
        during,
        /** Hold through the complete transport operation, including response parsing. */
        async guard(init, operation) {
            return /^(GET|HEAD)$/i.test(init.method ?? 'GET') ? operation() : during(operation);
        },
    };
}
//# sourceMappingURL=app-update.js.map